/**
 * Compute LCS table for two arrays of items.
 * Returns a 2D table where table[i][j] = length of LCS of a[0..i-1] and b[0..j-1].
 */
function lcsTable(a, b, eq) {
    const m = a.length;
    const n = b.length;
    const table = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (eq(a[i - 1], b[j - 1])) {
                table[i][j] = table[i - 1][j - 1] + 1;
            }
            else {
                table[i][j] = Math.max(table[i - 1][j], table[i][j - 1]);
            }
        }
    }
    return table;
}
/**
 * Backtrack through LCS table to produce diff operations.
 */
function backtrack(table, a, b, eq) {
    const result = [];
    let i = a.length;
    let j = b.length;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && eq(a[i - 1], b[j - 1])) {
            result.push({ type: 'unchanged', value: a[i - 1] });
            i--;
            j--;
        }
        else if (j > 0 && (i === 0 || table[i][j - 1] >= table[i - 1][j])) {
            result.push({ type: 'added', value: b[j - 1] });
            j--;
        }
        else {
            result.push({ type: 'removed', value: a[i - 1] });
            i--;
        }
    }
    return result.reverse();
}
/**
 * Compute a line-level diff between two texts.
 * Returns an array of DiffHunks with line numbers relative to the original text.
 */
export function computeLineDiff(textA, textB) {
    if (textA === textB) {
        if (textA === '')
            return [];
        return textA.split('\n').map((line, i) => ({
            type: 'unchanged',
            content: line,
            lineNumber: i + 1,
        }));
    }
    const linesA = textA === '' ? [] : textA.split('\n');
    const linesB = textB === '' ? [] : textB.split('\n');
    const table = lcsTable(linesA, linesB, (a, b) => a === b);
    const ops = backtrack(table, linesA, linesB, (a, b) => a === b);
    let lineA = 0;
    let lineB = 0;
    const hunks = [];
    for (const op of ops) {
        switch (op.type) {
            case 'unchanged':
                lineA++;
                lineB++;
                hunks.push({ type: 'unchanged', content: op.value, lineNumber: lineA });
                break;
            case 'removed':
                lineA++;
                hunks.push({ type: 'removed', content: op.value, lineNumber: lineA });
                break;
            case 'added':
                lineB++;
                hunks.push({ type: 'added', content: op.value, lineNumber: lineB });
                break;
        }
    }
    return hunks;
}
/**
 * Compute a word-level diff between two lines.
 * Splits on whitespace boundaries, preserving whitespace in the output.
 */
export function computeWordDiff(lineA, lineB) {
    if (lineA === lineB) {
        if (lineA === '')
            return [];
        return [{ type: 'unchanged', text: lineA }];
    }
    // Split into words while preserving spacing
    const splitWords = (s) => {
        const result = [];
        let current = '';
        for (const ch of s) {
            if (ch === ' ' || ch === '\t') {
                if (current.length > 0) {
                    result.push(current);
                    current = '';
                }
                result.push(ch);
            }
            else {
                current += ch;
            }
        }
        if (current.length > 0) {
            result.push(current);
        }
        return result;
    };
    const wordsA = splitWords(lineA);
    const wordsB = splitWords(lineB);
    const table = lcsTable(wordsA, wordsB, (a, b) => a === b);
    const ops = backtrack(table, wordsA, wordsB, (a, b) => a === b);
    // Merge consecutive ops of same type
    const segments = [];
    for (const op of ops) {
        const last = segments[segments.length - 1];
        if (last && last.type === op.type) {
            last.text += op.value;
        }
        else {
            segments.push({ type: op.type, text: op.value });
        }
    }
    return segments;
}
//# sourceMappingURL=diff.js.map